import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import decline from 'utils/decline';
import { getStringFullDate } from 'utils/getStringDate';
import getNameTransportType from 'utils/getNameTransportType';
import isEmpty from 'lodash/isEmpty';
import Button from 'ati-ui-react/components/Button';
import HeaderWithIcon from 'ati-ui-react/components/HeaderWithIcon';
import { useStores } from 'hooks/useStores';
import Modal from 'ati-ui-react/components/Popup';
import ComplaintPopup from 'generic/ComplaintPopup';
import ConfirmationPopup from './components/ConfirmationPopup';

import { ResultsCheckVinContainer, HeaderContainer, Header } from './ResultsCheckVinStyles';
import avtokodSrc from './img/avtokod.svg';

import FullVinReport from '../FullVinReport';

const ResultsCheckVin = observer(() => {
  const {
    checksStore: {
      getTransportShortReport,
      transportShortReport,
      transportShortReport: {
        valid_characteristics,
        owners_count,
        has_restrictions,
        has_accidents,
        date_creation,
      },
      buyingFullReportStatus,
    },
    profileStore: { isUser },
    popupStore: { openLoginPopup },
    advertsStore: {
      currentAdvert: { advert_items, full_report_available, full_report_exists, url },
    },
  } = useStores();

  useEffect(() => {
    getTransportShortReport();
  }, []);

  const [isOpenedBuyingPopup, setOpenedBuyingPopup] = useState(false);
  const openBuyingPopup = () => setOpenedBuyingPopup(true);
  const closeBuyingPopup = () => setOpenedBuyingPopup(false);

  const [isOpenedFullReport, setOpenedFullReport] = useState(false);
  const openFullReport = () => setOpenedFullReport(true);
  const closeFullReport = () => setOpenedFullReport(false);

  const [isOpenedComplaintPopup, setOpenedComplaintPopup] = useState(false);
  const openComplaintPopup = () => {
    setOpenedComplaintPopup(true);
    closeFullReport();
  };
  const closeComplaintPopup = () => {
    setOpenedComplaintPopup(false);
    openFullReport();
  };

  const neededKeys = ['valid_characteristics', 'has_restrictions', 'has_accidents', 'owners_count'];
  const transportName = advert_items && getNameTransportType(advert_items[0].type).toLowerCase();
  const isCheckInProgress = neededKeys.some((key) =>
    Object.keys(transportShortReport).includes(key),
  );

  useEffect(() => {
    if (buyingFullReportStatus.isSuccess) {
      closeBuyingPopup();
      openFullReport();
    }
  }, [buyingFullReportStatus.state]);

  // Не показываем компонент если нет поля transport_short_report или все поля в прогрессе получения информации
  if (isEmpty(transportShortReport) || !isCheckInProgress) {
    return null;
  }

  const owners = owners_count && owners_count;

  const characteristicsText = valid_characteristics
    ? 'Характеристики совпадают с ПТС'
    : 'Характеристики не совпадают с ПТС';

  const ownersCountText =
    owners_count && `${owners} ${decline(owners, ['владелец', 'владельца', 'владельцев'])} по ПТС`;

  const restrictionsText = has_restrictions
    ? 'Есть юридические ограничения'
    : 'Ограничений не найдено';

  const accidentsText = has_accidents ? 'Обнаружено ДТП' : 'ДТП не обнаружено';

  const checkValid = [valid_characteristics, !has_restrictions, !has_accidents];

  const handleOpenReport = () => {
    if (!isUser) {
      openLoginPopup();
    } else if (full_report_available) {
      openFullReport();
    } else {
      openBuyingPopup();
    }
  };

  return (
    <ResultsCheckVinContainer isValid={checkValid.includes(false)}>
      <HeaderContainer>
        <Header>Проверка {advert_items && `${transportName}а`} по VIN</Header>
        <img src={avtokodSrc} alt="autocod" className="autocod-icon" />
      </HeaderContainer>
      <span className="date-creation">
        Отчет составлен {date_creation && getStringFullDate(date_creation)}
      </span>
      <div className="results-descr-container">
        {'valid_characteristics' in transportShortReport && (
          <HeaderWithIcon
            size="small"
            className="results-descr"
            text={characteristicsText}
            type={valid_characteristics ? 'success' : 'error'}
          />
        )}
        {!!owners_count && (
          <HeaderWithIcon
            size="small"
            className="results-descr"
            text={ownersCountText}
            type={owners_count ? 'success' : 'warning'}
          />
        )}
        {'has_restrictions' in transportShortReport && (
          <HeaderWithIcon
            size="small"
            className="results-descr"
            text={restrictionsText}
            type={!has_restrictions ? 'success' : 'error'}
          />
        )}
        {'has_accidents' in transportShortReport && (
          <HeaderWithIcon
            size="small"
            className="results-descr"
            text={accidentsText}
            type={!has_accidents ? 'success' : 'error'}
          />
        )}
      </div>
      {full_report_exists && (
        <>
          <Button className="open-full-report_btn" onClick={handleOpenReport}>
            {full_report_available ? 'Открыть отчет' : 'Получить полный отчет'}
          </Button>
          {isOpenedBuyingPopup && <ConfirmationPopup close={closeBuyingPopup} />}
          {isOpenedFullReport && (
            <Modal title="Отчет о проверке по VIN" onClose={closeFullReport} closeOnOverlayClick>
              <FullVinReport openComplaintPopup={openComplaintPopup} close={closeFullReport} />
            </Modal>
          )}
          {isOpenedComplaintPopup && (
            <ComplaintPopup
              transportUrl={url}
              onClose={closeComplaintPopup}
              type="report"
              title="Сообщить о неточных данных"
              isGoBack
            />
          )}
        </>
      )}
    </ResultsCheckVinContainer>
  );
});

export default ResultsCheckVin;
